import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { useSnackbar } from 'notistack'
import { colors } from 'src/components/app-ui'
import { PatchContent, PatchData } from 'src/model/patch'
import { patchCourseCollectionAction } from 'src/reducers/collection/action'

const useStyles = makeStyles({
  row: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    maxWidth: '100%',
    '&:hover $edit': { opacity: 1 },
  },
  name: {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  edit: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    flex: 'none',
    padding: 0,
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: colors.muted,
    cursor: 'pointer',
    opacity: 0.7,
    '&:hover': { background: colors.hover, color: colors.navy },
  },
  // Same type as the page title, so the row doesn't jump when editing.
  input: {
    width: 'min(560px, 100%)',
    margin: 0,
    padding: '2px 8px',
    border: `1px solid ${colors.primary}`,
    borderRadius: 8,
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    background: '#FFFFFF',
  },
})

/**
 * The path's name as the page title, renamed in place: pencil (or a click on
 * the name), then Enter or leaving the field saves, Escape cancels.
 */
const PathTitle = ({ collection, editable }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const name = collection?.name || 'Untitled learning path'
  const [draft, setDraft] = useState(null)
  const inputRef = useRef(null)
  // Escape removes the field, which can still fire its blur: don't save then.
  const cancelled = useRef(false)
  const editing = draft !== null

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  if (!editable) return name

  const save = () => {
    if (cancelled.current) {
      cancelled.current = false
      return
    }
    const next = (draft || '').trim()
    setDraft(null)
    if (!next || next === collection?.name) return
    const patch = new PatchData(null, collection.id)
    patch.addContent(
      new PatchContent(next, 'collectionName', PatchData.OPERATION_REPLACE),
    )
    dispatch(
      patchCourseCollectionAction(patch, () =>
        enqueueSnackbar('Saved', { variant: 'success' }),
      ),
    )
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={classes.input}
        value={draft}
        maxLength={200}
        aria-label="Learning path name"
        onChange={event => setDraft(event.target.value)}
        onBlur={save}
        onKeyDown={event => {
          if (event.key === 'Enter') event.currentTarget.blur()
          if (event.key === 'Escape') {
            cancelled.current = true
            setDraft(null)
          }
        }}
      />
    )
  }

  return (
    <span className={classes.row}>
      <span className={classes.name}>{name}</span>
      <button
        type="button"
        className={classes.edit}
        title="Rename learning path"
        aria-label="Rename learning path"
        onClick={() => setDraft(collection?.name || '')}
      >
        <Icon icon="tabler:pencil" width={18} height={18} />
      </button>
    </span>
  )
}

export default PathTitle
